<script lang="ts" setup>
import { currencyFormatter } from '@tools/helpers';
import { useVModel } from '@vueuse/core';
import { computed } from 'vue';
import { type Maybe } from '../types';

const props = withDefaults(
  defineProps<{
    autocomplete?: boolean;
    disabled?: boolean;
    hasError?: boolean;
    id?: string;
    name?: string;
    noDecimals?: boolean;
    placeholder?: string;
    readonly?: boolean;
    value?: Maybe<number>;
  }>(),
  {
    value: undefined,
    name: undefined,
    placeholder: '€ 0,00',
    readonly: false,
    disabled: false,
    autocomplete: true,
    hasError: false,
    noDecimals: false,
  },
);

const emit = defineEmits<{
  'update:value': [Maybe<number>];
  blur: [];
}>();

const allowedKeys = computed(() => [
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  'Tab',
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  ...((props.noDecimals && []) || [',']),
]);

const data = useVModel(props, 'value', emit);

function handleUpdate(event: Event) {
  const { value } = event.target as HTMLInputElement;
  const number = value
    .trim()
    .replaceAll('.', '')
    .replace(',', '.')
    .replace(/[^0-9.]+/, '');

  const floatValue = parseFloat(number);
  if (Number.isNaN(floatValue)) {
    data.value = undefined;
    return;
  }
  data.value = parseFloat(floatValue.toFixed(2));
}

function checkIfNumber(event: KeyboardEvent) {
  // Sta sneltoetsen toe zoals Ctrl+C, Ctrl+V etc..
  if (event.ctrlKey || event.metaKey) return;
  // De numpad heeft geen komma: vertaal de punt naar het decimaalteken
  if (!props.noDecimals && event.key === '.') {
    event.preventDefault();
    insertDecimaalteken(event.target as HTMLInputElement);
    return;
  }
  if (!allowedKeys.value.includes(event.key)) {
    event.preventDefault();
  }
}

function insertDecimaalteken(input: HTMLInputElement) {
  const start = input.selectionStart ?? input.value.length;
  const end = input.selectionEnd ?? start;
  input.value = `${input.value.slice(0, start)},${input.value.slice(end)}`;
  input.setSelectionRange(start + 1, start + 1);
  input.dispatchEvent(new Event('input', { bubbles: true }));
}

const formattedValue = computed(() =>
  data.value === undefined ? '' : currencyFormatter(data.value, 2),
);
</script>

<template>
  <div
    :class="[
      'relative flex flex-1',
      {
        '*:border-red *:bg-red/10': hasError,
      },
    ]"
    data-testid="currency-field-input"
  >
    <template v-if="!readonly">
      <input
        :autocomplete="autocomplete ? 'on' : 'off'"
        :class="[
          'styled-input peer [&:autofill:not(:focus)]:![-webkit-text-fill-color:_transparent] [&:not(:focus)]:!text-transparent selection:[&:not(:focus)]:text-transparent',
          {
            filled: data,
          },
        ]"
        :disabled="disabled"
        :id="props.id"
        :name="name"
        :placeholder="props.placeholder"
        type="text"
        :value="data ? String(data)?.replace('.', ',') : data"
        @input="handleUpdate"
        @keydown="checkIfNumber"
        @blur="emit('blur')"
      />
      <div
        class="pointer-events-none absolute inset-0 flex items-center overflow-hidden px-[.5625em] peer-focus:hidden"
        :class="{ 'text-grey-400': disabled }"
      >
        {{ formattedValue }}
      </div>
    </template>
    <div v-else class="filled group-[]/float-label:h-11 group-[]/float-label:content-center">
      {{ formattedValue || '-' }}
    </div>
  </div>
</template>
